import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getCurrencySymbol, getDefaultCurrency } from "@/utils/currency";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import * as XLSX from "xlsx";
import { downloadBlob } from "@/utils/download";

export function useCmv() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [report, setReport] = useState(null);
  const [cashPosition, setCashPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const [cmvRes, cashRes] = await Promise.all([
        authAxiosRef.current.get(`/financiero/cmv?month=${month}`),
        authAxiosRef.current.get(`/financiero/cash-position?month=${month}`),
      ]);
      setReport(cmvRes.data);
      setCashPosition(cashRes.data);
    } catch {} finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]); // month is intentional: refetch when user changes month

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const saveSnapshot = useCallback(async (targetMonth, openingOverride, closingOverride) => {
    try {
      const body = { month: targetMonth };
      if (openingOverride !== undefined) body.opening_balance = parseFloat(openingOverride);
      if (closingOverride !== undefined) body.closing_balance = parseFloat(closingOverride);
      await authAxiosRef.current.post("/financiero/cash-position/snapshot", body);
      await fetchReport();
      toast.success(`Fecho de ${targetMonth} confirmado.`);
    } catch {
      toast.error("Erro ao guardar fecho de mês.");
    }
  }, [fetchReport]);

  const formatPrice = (v) => {
    const sym = getCurrencySymbol(getDefaultCurrency().currency);
    return `${(v || 0).toFixed(2)}${sym}`;
  };
  const barColor = (pct) => pct > 35 ? "#ef4444" : pct > 25 ? "#f59e0b" : "#22c55e";

  const exportPDF = () => {
    if (!report) return;
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.setTextColor(5, 150, 105);
    doc.text("KAISO SYSTEM", 14, 20);
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`Demonstrativo de Resultados (DRE) - ${month}`, 14, 30);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Gerado em: ${new Date().toLocaleDateString("pt-BR")}`, 14, 37);

    doc.setFontSize(12);
    doc.setTextColor(5, 150, 105);
    doc.text(`Faturamento: ${formatPrice(report.revenue)} (${report.orders_count} pedidos)`, 14, 50);

    let y = 60;
    if (report.materia_prima.length > 0) {
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.text("CMV - Matéria Prima", 14, y);
      autoTable(doc, {
        startY: y + 3,
        head: [["Categoria", "Total", "% Receita"]],
        body: report.materia_prima.map(i => [i.label, formatPrice(i.total), `${i.pct}%`]),
        foot: [["Total Matéria Prima", formatPrice(report.total_materia_prima), `${report.pct_materia_prima}%`]],
        theme: "striped",
        headStyles: { fillColor: [5, 150, 105] },
        footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: "bold" },
        margin: { left: 14 },
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    if (report.custos_fixos.length > 0) {
      doc.setTextColor(0, 0, 0);
      doc.text("Custos Fixos e Operacionais", 14, y);
      autoTable(doc, {
        startY: y + 3,
        head: [["Categoria", "Total", "% Receita"]],
        body: report.custos_fixos.map(i => [i.label, formatPrice(i.total), `${i.pct}%`]),
        foot: [["Total Custos Fixos", formatPrice(report.total_custos_fixos), `${report.pct_custos_fixos}%`]],
        theme: "striped",
        headStyles: { fillColor: [99, 102, 241] },
        footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: "bold" },
        margin: { left: 14 },
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    autoTable(doc, {
      startY: y,
      head: [["", "Valor", "% Receita"]],
      body: [
        ["Faturamento", formatPrice(report.revenue), "100%"],
        ["(-) Matéria Prima", formatPrice(report.total_materia_prima), `${report.pct_materia_prima}%`],
        ["(-) Custos Fixos", formatPrice(report.total_custos_fixos), `${report.pct_custos_fixos}%`],
        ["(=) Resultado Líquido", formatPrice(report.result_liquid), `${report.pct_result}%`],
      ],
      theme: "grid",
      headStyles: { fillColor: [30, 30, 30] },
      bodyStyles: { fontSize: 11 },
      margin: { left: 14 },
      didParseCell: (data) => {
        if (data.row.index === 3) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = report.result_liquid >= 0 ? [209, 250, 229] : [254, 226, 226];
        }
      },
    });

    const finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text("Gerado automaticamente por Kaiso System", 14, finalY);

    downloadBlob(doc.output("blob"), `DRE_${month}.pdf`);
    toast.success("PDF exportado!");
  };

  const exportExcelCMV = () => {
    if (!report) return;
    const rows = [
      { Tipo: "Faturamento", Categoria: "", Total: report.revenue, Percentual: "100%" },
      ...report.materia_prima.map(i => ({ Tipo: "Matéria Prima", Categoria: i.label, Total: i.total, Percentual: `${i.pct}%` })),
      { Tipo: "Total Matéria Prima", Categoria: "", Total: report.total_materia_prima, Percentual: `${report.pct_materia_prima}%` },
      ...report.custos_fixos.map(i => ({ Tipo: "Custo Fixo", Categoria: i.label, Total: i.total, Percentual: `${i.pct}%` })),
      { Tipo: "Total Custos Fixos", Categoria: "", Total: report.total_custos_fixos, Percentual: `${report.pct_custos_fixos}%` },
      { Tipo: "Resultado Líquido", Categoria: "", Total: report.result_liquid, Percentual: `${report.pct_result}%` },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DRE");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    downloadBlob(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `DRE_${month}.xlsx`
    );
    toast.success("Excel exportado!");
  };

  return { report, cashPosition, loading, month, setMonth, formatPrice, barColor, exportPDF, exportExcelCMV, saveSnapshot, fetchReport };
}
